import { useTranslation } from 'react-i18next';
import { Box, ErrorText, Select, SelectOption, TextField, Radio, withStyles } from 'vopay';
import { WeekdayToggleGroup } from 'UI';
import type { WeekdayCode } from 'UI';
import styles from './CustomRecurrenceFields.module.scss';

const cn = withStyles(styles);

export type CustomRecurrenceUnit = 'day' | 'week' | 'month' | 'year';
export type CustomRecurrenceMonthlyRepeatOn = 'dayOfMonth' | 'ordinalWeekday';

const CUSTOM_RECURRENCE_UNITS: string[] = ['day', 'week', 'month', 'year'];
function isCustomRecurrenceUnit(value: string): value is CustomRecurrenceUnit {
  return CUSTOM_RECURRENCE_UNITS.includes(value);
}

const MONTHLY_REPEAT_ON_VALUES: string[] = ['dayOfMonth', 'ordinalWeekday'];
function isMonthlyRepeatOn(value: string): value is CustomRecurrenceMonthlyRepeatOn {
  return MONTHLY_REPEAT_ON_VALUES.includes(value);
}

export type CustomRecurrenceValue = {
  interval: number;
  unit: CustomRecurrenceUnit;
  weekdays: WeekdayCode[];
  monthlyRepeatOn: CustomRecurrenceMonthlyRepeatOn;
};

export type CustomRecurrenceFieldsProps = {
  value: CustomRecurrenceValue;
  onChange: (value: CustomRecurrenceValue) => void;
  // Pre-formatted "Monthly on day 25" / "Monthly on the fourth Monday" labels, computed by the caller.
  monthlyRepeatOnLabels: { dayOfMonth: string; ordinalWeekday: string };
  disabled?: boolean;
  intervalError?: string | null;
};

// Custom-preset-only fields of the Recurring Payment card. No "Ends" section or card wrapper
// here — see RecurrenceEndsField / RecurringPaymentCard, shared by every preset.
export default function CustomRecurrenceFields({
  value,
  onChange,
  monthlyRepeatOnLabels,
  disabled,
  intervalError,
}: CustomRecurrenceFieldsProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'UI.CustomRecurrenceFields' });

  const unitOptions: SelectOption<{ label: string; value: string }>[] = [
    { label: t('Unit.day', { defaultValue: 'day' }), value: 'day' },
    { label: t('Unit.week', { defaultValue: 'week' }), value: 'week' },
    { label: t('Unit.month', { defaultValue: 'month' }), value: 'month' },
    { label: t('Unit.year', { defaultValue: 'year' }), value: 'year' },
  ];

  return (
    <>
      <Box flx="col" ai="fs" gapSize="8px" className={cn('row')}>
        <span>
          {t('repeatEvery', { defaultValue: 'Repeat every' })} <span className={cn('requiredAsterisk')}>*</span>
        </span>
        <Box ai="center" gapSize="16px" width="100%">
          <div className={cn('intervalInput')}>
            <TextField
              variant="outlineShadow"
              preset="numericWholePositive"
              name="interval"
              value={value.interval || ''}
              disabled={disabled}
              onChange={(e) => onChange({ ...value, interval: Number(e.target.value) || 0 })}
            />
          </div>
          <div className={cn('unitSelect')}>
            <Select
              version="2"
              name="unit"
              value={value.unit}
              disabled={disabled}
              onChange={(e) => {
                if (isCustomRecurrenceUnit(e.target.value)) onChange({ ...value, unit: e.target.value });
              }}
              options={unitOptions}
            />
          </div>
        </Box>
        <ErrorText error={intervalError} size="sm" fitContent />
      </Box>

      {value.unit === 'week' && (
        <Box flx="col" ai="fs" gapSize="8px" className={cn('row')}>
          <span>
            {t('repeatOn', { defaultValue: 'Repeat on' })} <span className={cn('requiredAsterisk')}>*</span>
          </span>
          <WeekdayToggleGroup
            value={value.weekdays}
            onChange={(weekdays) => onChange({ ...value, weekdays })}
            disabled={disabled}
          />
        </Box>
      )}

      {value.unit === 'month' && (
        <Box flx="col" ai="fs" gapSize="8px" className={cn('row')}>
          <span>
            {t('repeatOn', { defaultValue: 'Repeat on' })} <span className={cn('requiredAsterisk')}>*</span>
          </span>
          <div className={cn('monthlyRepeatOnRadioGroup')}>
            <Radio
              name="customRecurrenceMonthlyRepeatOn"
              value={value.monthlyRepeatOn}
              disabled={disabled}
              onChange={(e) => {
                if (isMonthlyRepeatOn(e.target.value)) onChange({ ...value, monthlyRepeatOn: e.target.value });
              }}
              options={{
                dayOfMonth: monthlyRepeatOnLabels.dayOfMonth,
                ordinalWeekday: monthlyRepeatOnLabels.ordinalWeekday,
              }}
            />
          </div>
        </Box>
      )}
    </>
  );
}
